import { useState, useMemo } from 'react'
import {
  Typography,
  Breadcrumb,
  Table,
  Button,
  Modal,
  Select,
  Input,
  Space,
  Tag,
  Badge,
  Tabs,
  Form,
  Popconfirm,
  Tooltip,
  Spin,
  message,
  Alert,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
  SendOutlined,
  CopyOutlined,
  DeleteOutlined,
  TeamOutlined,
  CheckOutlined,
  UndoOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { FilterValue, SorterResult, TablePaginationConfig } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ru'

import { useAuthStore } from '@/store/auth'
import { useUsers, useUpdateUser, useDeactivateUser, useReactivateUser, useDeleteUser, useAnonymizeUser, useOrganizations, useUserAccessGroups } from '@/hooks/useUsers'
import { useAccessGroups, useAddMember, useRemoveMember } from '@/hooks/useAccessGroups'
import { useInvites, useCreateInvite, useRevokeInvite } from '@/hooks/useInvites'
import { useQueryClient } from '@tanstack/react-query'
import type { UserItem, UserListParams } from '@/api/users'
import type { InviteItem } from '@/api/invites'
import UserRlsTab from './UserRlsTab'
import UserIdentityTab from './UserIdentityTab'
import CreateUserWizard from './CreateUserWizard'
import { ROLE_COLORS, ROLE_LABELS, getAssignableRoles } from '@/utils/userRoles'

dayjs.extend(relativeTime)
dayjs.locale('ru')

const { Title } = Typography
const { Search } = Input

const LOGIN_MODE_OPTIONS = [
  { value: 'yandex_sso', label: 'Ожидается Yandex SSO' },
  { value: 'local_or_manual', label: 'Local / manual' },
]

const LOGIN_READINESS_OPTIONS = [
  { value: 'yandex_sso_ready', label: 'Yandex: готов', color: 'green' },
  { value: 'yandex_link_missing', label: 'Yandex: link отсутствует', color: 'red' },
  { value: 'manual_review_required', label: 'Yandex: manual review', color: 'gold' },
  { value: 'missing_zitadel_user', label: 'Yandex: нет Zitadel', color: 'volcano' },
  { value: 'local_password_ready', label: 'Local: password ready', color: 'blue' },
  { value: 'local_account_only', label: 'Local: account only', color: 'default' },
  { value: 'local_manual_flow', label: 'Local: manual flow', color: 'default' },
  { value: 'status_unavailable', label: 'Статус недоступен', color: 'purple' },
]

function getLoginReadinessMeta(record: UserItem) {
  const matched = LOGIN_READINESS_OPTIONS.find((item) => item.value === record.login_readiness_status)
  return {
    label: record.login_readiness_label || matched?.label || '—',
    color: matched?.color || 'default',
  }
}

// ── Expandable row: user's access groups ──────────────────────────────────────

function UserGroupsExpanded({ userId }: { userId: string }) {
  const { data: userGroups, isLoading } = useUserAccessGroups(userId)
  const { data: allGroups } = useAccessGroups()
  const addMemberMutation = useAddMember()
  const removeMemberMutation = useRemoveMember()
  const queryClient = useQueryClient()
  const [addGroupId, setAddGroupId] = useState<string | undefined>(undefined)

  // Draft mode state
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(new Set())
  const [pendingAdditions, setPendingAdditions] = useState<{ id: string; name: string }[]>([])
  const [applying, setApplying] = useState(false)

  const hasChanges = pendingRemovals.size > 0 || pendingAdditions.length > 0

  // Groups the user is NOT yet a member of (also exclude pending additions)
  const availableGroups = useMemo(() => {
    if (!allGroups || !userGroups) return []
    const memberIds = new Set(userGroups.map((g) => g.id))
    const pendingAddIds = new Set(pendingAdditions.map((g) => g.id))
    return allGroups.filter((g) => g.is_active && !memberIds.has(g.id) && !pendingAddIds.has(g.id))
  }, [allGroups, userGroups, pendingAdditions])

  // Draft: mark group for removal (visual only)
  const handleMarkRemoval = (groupId: string) => {
    setPendingRemovals((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId) // toggle off
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  // Draft: add group locally
  const handleDraftAdd = () => {
    if (!addGroupId || !allGroups) return
    const group = allGroups.find((g) => g.id === addGroupId)
    if (!group) return
    setPendingAdditions((prev) => [...prev, { id: group.id, name: group.name }])
    setAddGroupId(undefined)
  }

  // Draft: remove from pending additions
  const handleUndoAdd = (groupId: string) => {
    setPendingAdditions((prev) => prev.filter((g) => g.id !== groupId))
  }

  // Apply all pending changes
  const handleApply = async () => {
    setApplying(true)
    try {
      // Execute removals
      for (const groupId of pendingRemovals) {
        await removeMemberMutation.mutateAsync({ groupId, userId })
      }
      // Execute additions
      for (const group of pendingAdditions) {
        await addMemberMutation.mutateAsync({ groupId: group.id, userId })
      }
      queryClient.invalidateQueries({ queryKey: ['user-access-groups', userId] })
      setPendingRemovals(new Set())
      setPendingAdditions([])
    } catch {
      // error handled by hooks
    } finally {
      setApplying(false)
    }
  }

  // Cancel all pending changes
  const handleCancel = () => {
    setPendingRemovals(new Set())
    setPendingAdditions([])
  }

  if (isLoading) {
    return (
      <div style={{ padding: '12px 48px' }}>
        <Spin size="small" /> <span style={{ marginLeft: 8, color: '#999' }}>Загрузка групп...</span>
      </div>
    )
  }

  return (
    <div style={{ padding: '8px 48px 12px' }}>
      <div style={{ marginBottom: 8 }}>
        <strong><TeamOutlined style={{ marginRight: 6 }} />Группы доступа</strong>
      </div>

      {/* Existing groups (with draft removal styling) + pending additions */}
      {(userGroups && userGroups.length > 0) || pendingAdditions.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {userGroups?.map((g) => {
            const isMarkedForRemoval = pendingRemovals.has(g.id)
            return (
              <Tag
                key={g.id}
                closable
                onClose={(e) => {
                  e.preventDefault()
                  handleMarkRemoval(g.id)
                }}
                style={{
                  marginRight: 0,
                  padding: '2px 8px',
                  ...(isMarkedForRemoval
                    ? { opacity: 0.4, textDecoration: 'line-through' }
                    : {}),
                }}
              >
                {g.name}
              </Tag>
            )
          })}
          {pendingAdditions.map((g) => (
            <Tag
              key={`add-${g.id}`}
              closable
              onClose={(e) => {
                e.preventDefault()
                handleUndoAdd(g.id)
              }}
              style={{
                marginRight: 0,
                padding: '2px 8px',
                borderColor: '#52c41a',
                borderStyle: 'solid',
                borderWidth: 1,
              }}
            >
              {g.name}
            </Tag>
          ))}
        </div>
      ) : (
        <div style={{ color: '#999', marginBottom: 12, fontSize: 13 }}>
          Не состоит ни в одной группе
        </div>
      )}

      <Space size="small">
        <Select
          placeholder="Добавить в группу..."
          allowClear
          style={{ width: 280 }}
          value={addGroupId}
          onChange={setAddGroupId}
          options={availableGroups.map((g) => ({
            value: g.id,
            label: g.name,
          }))}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          notFoundContent="Нет доступных групп"
        />
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          disabled={!addGroupId}
          onClick={handleDraftAdd}
        >
          Добавить
        </Button>
      </Space>

      {/* Apply / Cancel buttons */}
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          disabled={!hasChanges}
          loading={applying}
          onClick={handleApply}
        >
          Применить
        </Button>
        <Button
          size="small"
          icon={<UndoOutlined />}
          disabled={!hasChanges}
          onClick={handleCancel}
        >
          Отменить
        </Button>
      </div>
    </div>
  )
}

// ── Main page component ──────────────────────────────────────────────────────

export default function UsersPage() {
  const authUser = useAuthStore((s) => s.user)
  const isSuperadmin = authUser?.role === 'superadmin'

  // ── Filters ──
  const [params, setParams] = useState<UserListParams>({ page: 1, page_size: 20 })
  const [inviteStatusFilter, setInviteStatusFilter] = useState<string | undefined>(undefined)

  // ── Modals ──
  const [createWizardOpen, setCreateWizardOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<UserItem | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [expandedTabsByUserId, setExpandedTabsByUserId] = useState<Record<string, string>>({})

  // ── Forms ──
  const [editForm] = Form.useForm()
  const [inviteForm] = Form.useForm()

  // ── Data hooks ──
  const { data: usersData, isLoading: usersLoading, isFetching: usersFetching } = useUsers(params)
  const { data: invitesData, isLoading: invitesLoading } = useInvites(inviteStatusFilter)
  const { data: organizations } = useOrganizations()

  const updateUserMutation = useUpdateUser()
  const deactivateMutation = useDeactivateUser()
  const reactivateMutation = useReactivateUser()
  const deleteMutation = useDeleteUser()
  const anonymizeMutation = useAnonymizeUser()
  const createInviteMutation = useCreateInvite()
  const revokeInviteMutation = useRevokeInvite()

  // ── Assignable roles ──
  const assignableRoles = useMemo(() => getAssignableRoles(isSuperadmin), [isSuperadmin])

  const orgOptions = useMemo(() => {
    return (organizations || []).map((o) => ({
      value: o.id,
      label: `${o.code} — ${o.name}`,
    }))
  }, [organizations])

  // ── Handlers ──
  const handleSearch = (value: string) => {
    setParams((p) => ({ ...p, search: value || undefined, page: 1 }))
  }

  const handleUsersTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserItem> | SorterResult<UserItem>[],
  ) => {
    const resolvedSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sorterField =
      resolvedSorter?.field === 'login_readiness_label'
        ? 'login'
        : typeof resolvedSorter?.field === 'string'
        ? resolvedSorter.field
        : undefined
    setParams((prev) => ({
      ...prev,
      page: pagination.current || 1,
      page_size: pagination.pageSize || prev.page_size,
      sort_by: sorterField,
      sort_order:
        resolvedSorter?.order === 'ascend'
          ? 'asc'
          : resolvedSorter?.order === 'descend'
          ? 'desc'
          : undefined,
    }))
  }

  const handleEditUser = async () => {
    if (!editingUser) return
    try {
      const values = await editForm.validateFields()
      // Remove unchanged fields
      const payload: Record<string, any> = {}
      if (values.role !== editingUser.role) payload.role = values.role
      if (values.org_id !== editingUser.org_id) payload.org_id = values.org_id
      if (values.full_name !== editingUser.full_name) payload.full_name = values.full_name
      if (values.email && values.email !== editingUser.email) payload.email = values.email

      if (Object.keys(payload).length > 0) {
        await updateUserMutation.mutateAsync({ userId: editingUser.id, data: payload })
      }
      setEditModalOpen(false)
      setEditingUser(null)
    } catch {
      // validation error
    }
  }

  const handleOpenEdit = (record: UserItem) => {
    setEditingUser(record)
    editForm.setFieldsValue({
      email: record.email,
      full_name: record.full_name,
      role: record.role,
      org_id: record.org_id,
    })
    setEditModalOpen(true)
  }

  const handleCreateInvite = async () => {
    try {
      const values = await inviteForm.validateFields()
      const result = await createInviteMutation.mutateAsync(values)
      const link = `${window.location.origin}/invite/${result.token}`
      setInviteLink(link)
    } catch {
      // validation error
    }
  }

  const handleCopyLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink)
      message.success('Ссылка скопирована')
    }
  }

  const handleOpenUserIdentity = (userId: string, email: string) => {
    setCreateWizardOpen(false)
    setParams((prev) => ({
      ...prev,
      page: 1,
      search: email,
      role: undefined,
      is_active: undefined,
      org_id: isSuperadmin ? undefined : prev.org_id,
    }))
    setExpandedRowKeys([userId])
    setExpandedTabsByUserId((prev) => ({ ...prev, [userId]: 'identity' }))
  }

  // ── Users table columns ──
  const userColumns: ColumnsType<UserItem> = [
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: true,
      ellipsis: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      ellipsis: true,
    },
    ...(isSuperadmin
      ? [
          {
            title: 'Организация',
            key: 'org',
            render: (_: any, record: UserItem) =>
              record.org_name || record.org_code || '\u2014',
            ellipsis: true,
          },
        ]
      : []),
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      width: 160,
      sorter: true,
      render: (role: string) => (
        <Tag color={ROLE_COLORS[role] || 'default'}>
          {ROLE_LABELS[role] || role}
        </Tag>
      ),
    },
    {
      title: 'Тип входа',
      dataIndex: 'login_readiness_label',
      key: 'login',
      width: 210,
      sorter: true,
      render: (_: unknown, record: UserItem) => {
        const meta = getLoginReadinessMeta(record)
        return (
          <Space direction="vertical" size={4}>
            <Space wrap>
              <Tag color={meta.color}>{meta.label}</Tag>
              <Tag color={record.login_yandex_expected ? 'blue' : 'default'}>
                {record.login_mode_label || (record.login_yandex_expected ? 'Ожидается Yandex SSO' : 'Local / manual')}
              </Tag>
            </Space>
            {record.login_yandex_expected && !record.login_yandex_linked ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                Требует onboarding
              </Typography.Text>
            ) : null}
          </Space>
        )
      },
    },
    {
      title: 'Доступ',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      sorter: true,
      render: (active: boolean) => (
        <Badge status={active ? 'success' : 'error'} text={active ? 'Открыт' : 'Закрыт'} />
      ),
    },
    {
      title: 'Последний вход',
      dataIndex: 'last_login',
      key: 'last_login',
      width: 150,
      sorter: true,
      render: (dt: string | null) =>
        dt ? (
          <Tooltip title={dayjs(dt).format('DD.MM.YYYY HH:mm')}>
            {dayjs(dt).fromNow()}
          </Tooltip>
        ) : (
          '\u2014'
        ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 180,
      render: (_: any, record: UserItem) => {
        const isSelf = record.id === authUser?.id
        return (
          <Space size="small">
            <Tooltip title="Редактировать">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenEdit(record)
                }}
              />
            </Tooltip>
            <Tooltip title="Логин и привязки (email aliases, Yandex link, auth identities)">
              <Button
                type="text"
                size="small"
                icon={<SafetyOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  setExpandedRowKeys([record.id])
                  setExpandedTabsByUserId((prev) => ({ ...prev, [record.id]: 'identity' }))
                }}
              />
            </Tooltip>
            {!isSelf && (
              record.is_active ? (
                <Popconfirm
                  title="Закрыть доступ сотруднику?"
                  onConfirm={() => deactivateMutation.mutate(record.id)}
                >
                  <Tooltip title="Деактивировать">
                    <Button type="text" size="small" danger icon={<StopOutlined />} onClick={(e) => e.stopPropagation()} />
                  </Tooltip>
                </Popconfirm>
              ) : (
                <Tooltip title="Активировать">
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    onClick={(e) => {
                      e.stopPropagation()
                      reactivateMutation.mutate(record.id)
                    }}
                  />
                </Tooltip>
              )
            )}
            {isSuperadmin && !isSelf && (
              <Popconfirm
                title="Удалить сотрудника?"
                description="Безвозвратно. Если есть история (audit, RLS, invites) — будет предложено anonymize. Сначала попробуйте; если 409 — используйте anonymize."
                okText="Удалить"
                okButtonProps={{ danger: true }}
                cancelText="Отмена"
                onConfirm={() =>
                  deleteMutation.mutate(record.id, {
                    onError: (error: any) => {
                      const status = error?.response?.status
                      const detail = error?.response?.data?.detail || ''
                      if (status === 409) {
                        Modal.confirm({
                          title: 'У сотрудника есть история',
                          content: `${detail || 'Удаление заблокировано.'} Анонимизировать? Это сохранит audit trail, но сотрёт PII и закроет доступ.`,
                          okText: 'Анонимизировать',
                          okButtonProps: { danger: true },
                          cancelText: 'Отмена',
                          onOk: () => anonymizeMutation.mutate(record.id),
                        })
                      }
                    },
                  })
                }
              >
                <Tooltip title="Удалить (только суперадмин)">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  // ── Invites table columns ──
  const inviteColumns: ColumnsType<InviteItem> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Организация',
      dataIndex: 'org_name',
      key: 'org_name',
      ellipsis: true,
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      width: 160,
      render: (role: string) => (
        <Tag color={ROLE_COLORS[role] || 'default'}>
          {ROLE_LABELS[role] || role}
        </Tag>
      ),
    },
    {
      title: 'Создал',
      dataIndex: 'creator_name',
      key: 'creator_name',
      ellipsis: true,
    },
    {
      title: 'Истекает',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 150,
      render: (dt: string) => dayjs(dt).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 130,
      render: (_: any, record: InviteItem) => {
        if (record.used_at) return <Tag color="green">Использовано</Tag>
        if (record.is_expired) return <Tag color="default">Истекло</Tag>
        return <Tag color="blue">Ожидает</Tag>
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: InviteItem) => {
        if (record.used_at || record.is_expired) return null
        return (
          <Space size="small">
            <Tooltip title="Копировать ссылку">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/invite/${record.token}`
                  )
                  message.success('Ссылка скопирована')
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Отозвать приглашение?"
              onConfirm={() => revokeInviteMutation.mutate(record.id)}
            >
              <Tooltip title="Отозвать">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Сотрудники' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Сотрудники
        </Title>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateWizardOpen(true)}
          >
            Добавить сотрудника
          </Button>
          <Button
            icon={<SendOutlined />}
            onClick={() => {
              inviteForm.resetFields()
              setInviteLink(null)
              if (!isSuperadmin && authUser?.orgId) {
                inviteForm.setFieldValue('org_id', authUser.orgId)
              }
              setInviteModalOpen(true)
            }}
          >
            Пригласить сотрудника
          </Button>
        </Space>
      </div>

      <Tabs
        defaultActiveKey="users"
        items={[
          {
            key: 'users',
            label: `Сотрудники (${usersData?.total ?? 0})`,
            children: (
              <>
                <Space style={{ marginBottom: 16 }} wrap>
                  <Search
                    placeholder="Поиск по ФИО или email"
                    allowClear
                    onSearch={handleSearch}
                    style={{ width: 250 }}
                  />
                  {isSuperadmin && (
                    <Select
                      placeholder="Организация"
                      allowClear
                      style={{ width: 250 }}
                      options={orgOptions}
                      onChange={(v) => setParams((p) => ({ ...p, org_id: v, page: 1 }))}
                    />
                  )}
                  <Select
                    placeholder="Роль"
                    allowClear
                    style={{ width: 180 }}
                    options={Object.entries(ROLE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
                    onChange={(v) => setParams((p) => ({ ...p, role: v, page: 1 }))}
                  />
                  <Select
                    placeholder="Доступ"
                    allowClear
                    style={{ width: 150 }}
                    options={[
                      { value: 'true', label: 'Открыт' },
                      { value: 'false', label: 'Закрыт' },
                    ]}
                    onChange={(v) =>
                      setParams((p) => ({
                        ...p,
                        is_active: v === undefined ? undefined : v === 'true',
                        page: 1,
                      }))
                    }
                  />
                  <Select
                    placeholder="Тип входа"
                    allowClear
                    style={{ width: 220 }}
                    options={LOGIN_MODE_OPTIONS}
                    onChange={(value) =>
                      setParams((p) => ({ ...p, login_mode: value, page: 1 }))
                    }
                  />
                  <Select
                    placeholder="Статус входа"
                    allowClear
                    style={{ width: 240 }}
                    options={LOGIN_READINESS_OPTIONS.map((item) => ({ value: item.value, label: item.label }))}
                    onChange={(value) =>
                      setParams((p) => ({ ...p, login_readiness_status: value, page: 1 }))
                    }
                  />
                </Space>
                <Table
                  columns={userColumns}
                  dataSource={usersData?.items || []}
                  rowKey="id"
                  loading={
                    usersLoading || usersFetching
                      ? { spinning: true, tip: 'Обновляем список и live-статусы входа...' }
                      : false
                  }
                  size="middle"
                  expandable={{
                    expandedRowKeys,
                    onExpand: (expanded, record) => {
                      setExpandedRowKeys((prev) =>
                        expanded ? Array.from(new Set([...prev, record.id])) : prev.filter((key) => key !== record.id)
                      )
                      if (expanded) {
                        setExpandedTabsByUserId((prev) => ({ ...prev, [record.id]: prev[record.id] || 'groups' }))
                      }
                    },
                    expandedRowRender: (record) => (
                      <Tabs
                        activeKey={expandedTabsByUserId[record.id] || 'groups'}
                        onChange={(activeKey) =>
                          setExpandedTabsByUserId((prev) => ({ ...prev, [record.id]: activeKey }))
                        }
                        items={[
                          {
                            key: 'groups',
                            label: 'Группы',
                            children: <UserGroupsExpanded userId={record.id} />,
                          },
                          {
                            key: 'rls',
                            label: 'RLS-привязки',
                            children: <UserRlsTab userId={record.id} lpuCode={record.org_code} />,
                          },
                          {
                            key: 'identity',
                            label: 'Логин / identity',
                            children: <UserIdentityTab userId={record.id} canonicalEmail={record.email} zitadelId={record.zitadel_id} />,
                          },
                        ]}
                      />
                    ),
                    expandRowByClick: true,
                  }}
                  pagination={{
                    current: params.page,
                    pageSize: params.page_size,
                    total: usersData?.total || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                  }}
                  onChange={handleUsersTableChange}
                />
              </>
            ),
          },
          {
            key: 'invites',
            label: `Приглашения (${invitesData?.total ?? 0})`,
            children: (
              <>
                <Space style={{ marginBottom: 16 }}>
                  <Select
                    placeholder="Фильтр"
                    allowClear
                    style={{ width: 200 }}
                    options={[
                      { value: 'pending', label: 'Ожидают' },
                      { value: 'used', label: 'Использованы' },
                      { value: 'expired', label: 'Истекшие' },
                    ]}
                    onChange={(v) => setInviteStatusFilter(v)}
                  />
                </Space>
                <Table
                  columns={inviteColumns}
                  dataSource={invitesData?.items || []}
                  rowKey="id"
                  loading={invitesLoading}
                  size="middle"
                  pagination={{ pageSize: 20 }}
                />
              </>
            ),
          },
        ]}
      />

      {/* Modal: Edit User */}
      <Modal
        title="Редактирование сотрудника"
        open={editModalOpen}
        onOk={handleEditUser}
        onCancel={() => { setEditModalOpen(false); setEditingUser(null) }}
        confirmLoading={updateUserMutation.isPending}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={editForm} layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Роль, ФИО, организация и canonical email"
            description="Доступ в BI определяется отдельно: группами доступа и персональными RLS-привязками. Назначать административные роли может только суперадмин. Email сохраняется в lowercase; Yandex SSO login управляется отдельно во вкладке «Логин и привязки»."
          />
          <Form.Item name="email" label="Email (canonical)" rules={[{ required: true, type: 'email', message: 'Введите корректный email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="full_name" label="ФИО" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Роль" rules={[{ required: true }]}>
            <Select
              options={assignableRoles.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
              disabled={editingUser?.id === authUser?.id}
            />
          </Form.Item>
          {isSuperadmin && (
            <Form.Item name="org_id" label="Организация">
              <Select
                placeholder="Выберите организацию"
                allowClear
                options={orgOptions}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <CreateUserWizard
        open={createWizardOpen}
        onClose={() => setCreateWizardOpen(false)}
        onOpenUserIdentity={handleOpenUserIdentity}
      />

      {/* Modal: Invite User */}
      <Modal
        title="Пригласить сотрудника"
        open={inviteModalOpen}
        onOk={inviteLink ? () => { setInviteModalOpen(false); setInviteLink(null); inviteForm.resetFields() } : handleCreateInvite}
        onCancel={() => { setInviteModalOpen(false); setInviteLink(null); inviteForm.resetFields() }}
        confirmLoading={createInviteMutation.isPending}
        okText={inviteLink ? 'Закрыть' : 'Создать приглашение'}
        cancelText="Отмена"
      >
        {inviteLink ? (
          <div>
            <p>Приглашение создано. Отправьте ссылку сотруднику:</p>
            <Input.Group compact>
              <Input
                style={{ width: 'calc(100% - 40px)' }}
                value={inviteLink}
                readOnly
              />
              <Button icon={<CopyOutlined />} onClick={handleCopyLink} />
            </Input.Group>
          </div>
        ) : (
          <Form form={inviteForm} layout="vertical">
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Введите корректный email' }]}>
              <Input placeholder="user@example.com" />
            </Form.Item>
            <Form.Item name="role" label="Роль" rules={[{ required: true, message: 'Выберите роль' }]}>
              <Select
                options={assignableRoles.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
              />
            </Form.Item>
            <Form.Item name="org_id" label="Организация" rules={[{ required: true, message: 'Выберите организацию' }]}>
              <Select
                placeholder="Выберите организацию"
                options={orgOptions}
                disabled={!isSuperadmin}
              />
            </Form.Item>
            <Form.Item name="expires_in_hours" label="Срок действия" initialValue={72}>
              <Select
                options={[
                  { value: 24, label: '24 часа' },
                  { value: 72, label: '3 дня' },
                  { value: 168, label: '7 дней' },
                  { value: 720, label: '30 дней' },
                ]}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  )
}
